import { deleteCurrentAuth } from "./SecurityService";
import { emitEvent } from "@/modules/EventService";

const config = {
  apiUrl: process.env.VUE_APP_API_URL,
  apiExternalUrl: process.env.VUE_APP_API_EXTERNAL_URL,
  apiUrlV2: process.env.VUE_APP_API_URL_V2,
  apiExternalUrlV2: process.env.VUE_APP_API_EXTERNAL_URL_V2,
  apiCreditCard: process.env.VUE_APP_API_CARD,
  apiTokenChat: process.env.VUE_APP_API_CHAT_TOKEN,
};

async function handleResponse<T>(response: Response) {
  const text: string = await response.text();
  if (response.status === 406) {
    return deleteCurrentAuth();
  }
  if (response.status === 401 || text === "jwt expired") {
    emitEvent("authError");
    return;
  }
  if (!response.ok) {
    return Promise.reject(text);
  }

  try {
    const data = JSON.parse(text);
    if (response.headers.get("x-access-token") !== null) {
      localStorage.setItem(
        "x-access-token",
        response.headers.get("x-access-token")!
      );
    }
    return Promise.resolve(data);
  } catch (ex) {
    return Promise.resolve(text);
  }
}

export const doGetRequest = async <T>(
  url: string,
  headers?: object
): Promise<T> => {
  const requestOptions = {
    method: "GET",
    headers: {
      ...headers,
    },
  };
  const response = await fetch(`${config.apiUrl}${url}`, requestOptions);
  return handleResponse<T>(response);
};

export const doPostRequestCreditCard = async <T>(
  url: string,
  payload?: any,
  headers?: object
): Promise<T> => {
  const requestOptions = {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
    },
    body: new URLSearchParams(payload),
  };

  const response = await fetch(`${config.apiCreditCard}${url}`, requestOptions);
  return handleResponse<T>(response);
};

export const doGetRequestStep = async <T>(
  url: string,
  query: any,
  headers?: object
): Promise<T> => {
  const requestOptions = {
    method: "GET",
    headers: {
      ...headers,
    },
    params: {
      purchaseOrderId: query,
    },
  };
  const response = await fetch(`${config.apiUrl}${url}`, requestOptions);
  return handleResponse<T>(response);
};

export const doGetExternalRequest = async <T>(
  url: string,
  headers?: object
): Promise<T> => {
  const requestOptions = {
    method: "GET",
    headers: {
      ...headers,
    },
  };
  const response = await fetch(`${url}`, requestOptions);
  return handleResponse<T>(response);
};

export const doGetExternalAppRequest = async <T>(
  url: string,
  headers?: object
): Promise<T> => {
  const requestOptions = {
    method: "GET",
    headers: {
      ...headers,
    },
  };
  const response = await fetch(
    `${config.apiExternalUrl}${url}`,
    requestOptions
  );
  return handleResponse<T>(response);
};

export const doGetExternalAppRequestV2 = async <T>(
  url: string,
  headers?: object
): Promise<T> => {
  const requestOptions = {
    method: "GET",
    headers: {
      ...headers,
    },
  };
  const response = await fetch(
    `${config.apiExternalUrl}${url}`,
    requestOptions
  );
  return handleResponse<T>(response);
};

export const doPostExternalAppRequest = async <T>(
  url: string,
  payload?: object,
  headers?: object
): Promise<T> => {
  const requestOptions = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...headers,
    },
    body: JSON.stringify(payload),
  };

  const response = await fetch(
    `${config.apiExternalUrl}${url}`,
    requestOptions
  );
  return handleResponse<T>(response);
};

export const doPostExternalAppRequestV2 = async <T>(
  url: string,
  payload?: object,
  headers?: object
): Promise<T> => {
  const requestOptions = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...headers,
    },
    body: JSON.stringify(payload),
  };

  const response = await fetch(
    `${config.apiExternalUrlV2}${url}`,
    requestOptions
  );
  return handleResponse<T>(response);
};

export const doPutExternalAppRequest = async <T>(
  url: string,
  payload?: object,
  headers?: object
): Promise<T> => {
  const requestOptions = {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      ...headers,
    },
    body: JSON.stringify(payload),
  };

  const response = await fetch(
    `${config.apiExternalUrl}${url}`,
    requestOptions
  );
  return handleResponse<T>(response);
};

// export const doSecureGetRequest = async <T>(
//   url: string,
//   headers?: object): Promise<T> => doGetRequest(url, {
//   ...authHeader(),
//   ...headers
// });

export const doPostRequest = async <T>(
  url: string,
  payload?: object,
  headers?: object
): Promise<T> => {
  const requestOptions = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...headers,
    },
    body: JSON.stringify(payload),
  };

  const response = await fetch(`${config.apiUrlV2}${url}`, requestOptions);
  return handleResponse<T>(response);
};

export const doPostRequestV2 = async <T>(
  url: string,
  payload?: object,
  headers?: object
): Promise<T> => {
  const requestOptions = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...headers,
    },
    body: JSON.stringify(payload),
  };

  const response = await fetch(`${config.apiUrlV2}${url}`, requestOptions);
  return handleResponse<T>(response);
};

export const doPostRequestV1 = async <T>(
  url: string,
  payload?: object,
  headers?: object
): Promise<T> => {
  const requestOptions = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...headers,
    },
    body: JSON.stringify(payload),
  };

  const response = await fetch(`${config.apiUrl}${url}`, requestOptions);
  return handleResponse<T>(response);
};

export const doPostChatToken = async <T>(
  url: string,
  payload?: object,
  headers?: object
): Promise<T> => {
  const requestOptions = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...headers,
    },
    body: JSON.stringify(payload),
  };
  const response = await fetch(`${config.apiTokenChat}${url}`, requestOptions);
  return handleResponse<T>(response);
};

export const doPutRequest = async <T>(
  url: string,
  payload?: object,
  headers?: object
): Promise<T> => {
  const requestOptions = {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      ...headers,
    },
    body: JSON.stringify(payload),
  };

  const response = await fetch(`${config.apiUrlV2}${url}`, requestOptions);
  return handleResponse<T>(response);
};

export const doPatchRequest = async <T>(
  url: string,
  payload?: object,
  headers?: object
): Promise<T> => {
  const requestOptions = {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      ...headers,
    },
    body: JSON.stringify(payload),
  };

  const response = await fetch(`${config.apiUrl}${url}`, requestOptions);
  return handleResponse<T>(response);
};

// export const doSecureFileUpload = async <T>(
//   url: string,
//   payload?: object): Promise<T> => {
//   const requestOptions = {
//     method: 'POST',
//     headers: {
//       ...(authHeader() as any)
//     },
//     body: payload
//   };
//
//   const response = await fetch(`${config.apiUrl}${url}`, requestOptions as any);
//   return handleResponse<T>(response);
// };

// export const doSecurePostRequest = <T>(
//   url: string,
//   payload?: object,
//   headers?: object): Promise<T> => doPostRequest<T>(url, payload, {
//     ...authHeader(),
//     ...headers
//   });
//
// export const doSecurePutRequest = async <T>(
//   url: string,
//   payload?: object,
//   headers?: object
// ): Promise<T> => {
//   const requestOptions = {
//     method: 'PUT',
//     headers: {
//       'Content-Type': 'application/json',
//       ...(authHeader() as any),
//       ...headers
//     },
//     body: JSON.stringify(payload)
//   };
//   const response = await fetch(`${config.apiUrl}${url}`, requestOptions);
//   return handleResponse<T>(response);
// };
//
// export const doSecureDeleteRequest = async <T>(
//   url: string,
//   headers?: object
// ): Promise<T> => {
//   const requestOptions = {
//     method: 'DELETE',
//     headers: {
//       ...authHeader(),
//       ...headers
//     }
//   };
//   const response = await fetch(`${config.apiUrl}${url}`, requestOptions as any);
//   return handleResponse<T>(response);
// };
